import { useState, useRef } from 'react';
import { Icon } from './Icons';

const SUGGESTIONS = [
  'Can she help grow our TikTok?',
  'What makes her short-form work different?',
  'Has she led a team?',
  'What tools does she use?',
];

export default function AskAgent({ agentLabel }) {
  const [q, setQ] = useState('');
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [tipMounted, setTipMounted] = useState(false);
  const [tipVisible, setTipVisible] = useState(false);
  const tipTimer = useRef(null);

  const onTipEnter = () => {
    clearTimeout(tipTimer.current);
    setTipMounted(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setTipVisible(true)));
  };
  const onTipLeave = () => {
    setTipVisible(false);
    tipTimer.current = setTimeout(() => setTipMounted(false), 220);
  };

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text || loading) return;
    setQ(text);
    setLoading(true);
    setError('');
    setAnswer('');
    setOpen(true);
    try {
      const res = await fetch('/.netlify/functions/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: text }),
      });
      if (res.status === 404) {
        setError("The assistant only runs on the live site (it needs the serverless function). Try it at makayla-richards.netlify.app.");
        setLoading(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) setError(data.error || 'Something went wrong. Try again?');
      else setAnswer(data.answer || '');
    } catch (e) {
      setError('Could not reach the assistant. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="no-print" style={{ marginBottom: 20 }}>
      {(agentLabel || 'Connect.') && (
        <div style={{
          fontFamily: "'Playfair Display', serif", fontSize: 14, fontStyle: 'italic',
          fontWeight: 700, color: 'var(--deep-rose)', marginBottom: 6, paddingLeft: 4,
        }}>
          {agentLabel || 'Connect.'}
        </div>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'white', border: '1.5px solid var(--border2)', borderRadius: 16,
        padding: '6px 6px 6px 16px', boxShadow: 'var(--shadow)',
      }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          onMouseEnter={onTipEnter} onMouseLeave={onTipLeave}>
          <Icon name="bulb" size={18} stroke="var(--deep-rose)" style={{ cursor: 'default' }} />
          {tipMounted && (
            <div style={{
              position: 'absolute', bottom: 'calc(100% + 10px)', left: '50%',
              transform: 'translateX(-50%)',
              width: 220, background: 'var(--ink)', color: 'rgba(255,255,255,.90)',
              borderRadius: 10, padding: '10px 13px',
              fontFamily: "'Nunito', sans-serif", fontSize: 13, lineHeight: 1.55,
              boxShadow: '0 8px 28px rgba(0,0,0,.22)',
              opacity: tipVisible ? 1 : 0,
              transition: 'opacity 0.18s ease',
              pointerEvents: 'none', zIndex: 99,
            }}>
              <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--rose)', marginBottom: 5 }}>// Knowledge base</div>
              This assistant knows Makayla's full work history, metrics, tools, methodologies, award wins, and content strategy frameworks. Ask anything about what she can do for you.
              <div style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid var(--ink)' }} />
            </div>
          )}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ask()}
          placeholder="See how Makayla Richards can solve your problem."
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontFamily: "'Nunito', sans-serif", fontSize: 15, color: 'var(--text)',
          }}
        />
        <button
          onClick={() => ask()}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 16px', borderRadius: 11, border: 'none',
            background: 'var(--crimson)', color: 'white',
            fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 800,
            cursor: loading ? 'wait' : 'pointer', flexShrink: 0,
          }}
        >
          {loading ? 'Thinking…' : 'Ask'}
        </button>
      </div>

      {/* Suggestions */}
      {!open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, paddingLeft: 4 }}>
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => ask(s)}
              style={{
                fontSize: 13, fontWeight: 700, padding: '4px 11px', borderRadius: 20,
                border: '1.5px solid var(--border2)', background: 'var(--warm)',
                color: 'var(--text2)', cursor: 'pointer', fontFamily: "'Nunito', sans-serif",
              }}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Answer panel */}
      {open && (
        <div style={{
          marginTop: 10, background: 'linear-gradient(135deg, var(--blush), var(--lavender))',
          border: '1.5px solid var(--rose)', borderRadius: 14, padding: '14px 16px', position: 'relative',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
            fontFamily: "'Courier Prime', monospace", fontSize: 10, letterSpacing: '.1em',
            textTransform: 'uppercase', color: 'var(--deep-rose)',
          }}>
            <span className="status-dot" style={{ background: 'var(--deep-rose)' }} /> Makayla's AI assistant
          </div>
          {loading && <div style={{ fontSize: 15, color: 'var(--text2)', fontStyle: 'italic' }}>Thinking it through…</div>}
          {error && <div style={{ fontSize: 14, color: 'var(--crimson)' }}>{error}</div>}
          {answer && <div style={{ fontSize: 15, color: 'var(--ink)', lineHeight: 1.6 }}>{answer}</div>}
          <button onClick={() => { setOpen(false); setAnswer(''); setError(''); }}
            style={{
              position: 'absolute', top: 10, right: 12, background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--deep-rose)',
            }}>
            clear
          </button>
          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text3)', fontStyle: 'italic' }}>
            AI-generated from Makayla's real work history. For specifics, email makayla.d.richards@gmail.com.
          </div>
        </div>
      )}
    </div>
  );
}
